/**
 * Custom Product Preview  v3
 * Asset: assets/custom-product-preview.js
 *
 * Changes from v2:
 *  - CTA panel replaced with an order form (name, email, message).
 *  - On submit: uploads preview image + original artwork to Dropbox via PKCE OAuth,
 *    then POSTs order details + Dropbox links to a configurable webhook URL.
 *  - "Save Preview as Image" download button retained separately.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     CSS
  ───────────────────────────────────────────── */
  const CSS = `
    .cpp-section * { box-sizing: border-box; }

    .cpp-header {
      text-align: center;
      padding: 0 20px 36px;
      max-width: 680px;
      margin: 0 auto;
    }
    .cpp-eyebrow {
      font-family: 'DM Sans', sans-serif;
      font-weight: 300;
      font-size: 11px;
      letter-spacing: 0.25em;
      text-transform: uppercase;
      color: #8b7355;
      margin-bottom: 10px;
    }
    .cpp-heading {
      font-family: var(--cpp-page-heading-font, 'Playfair Display', serif) !important;
      font-size: var(--cpp-page-heading-size, clamp(26px, 4vw, 44px)) !important;
      font-weight: 400 !important;
      color: #1a1208;
      line-height: 1.15 !important;
      margin: 0 0 12px !important;
    }
    .cpp-heading em { font-style: italic; color: #8b7355; }
    .cpp-subheading {
      font-family: var(--cpp-subheading-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-subheading-size, 14px);
      color: #5c4a30;
      font-weight: 300;
      line-height: 1.7;
      margin: 0;
    }

    .cpp-layout {
      display: grid;
      grid-template-columns: 1fr 320px;
      gap: 28px;
      max-width: 1100px;
      margin: 0 auto;
      padding: 0 24px;
      align-items: start;
    }
    @media (max-width: 820px) {
      .cpp-layout { grid-template-columns: 1fr; }

      /* Collapsed product panel — mobile only */
      .cpp-panel.cpp-panel--collapsible .cpp-panel-header {
        cursor: pointer;
        user-select: none;
      }
      .cpp-panel.cpp-panel--collapsible .cpp-panel-header .cpp-panel-chevron {
        margin-left: auto;
        font-size: 10px;
        color: var(--cpp-panel-header-text, #5c4a30);
        transition: transform 0.2s;
        flex-shrink: 0;
      }
      .cpp-panel.cpp-panel--collapsible.collapsed .cpp-panel-chevron {
        transform: rotate(-90deg);
      }
      .cpp-panel.cpp-panel--collapsible.collapsed .cpp-panel-body {
        display: none;
      }
    }

    /* ── Canvas ── */
    .cpp-canvas-area {
      background: #fff;
      border: 1px solid var(--cpp-chrome-border, #d4c9b0);
      border-radius: 4px;
      padding: 20px;
    }
    .cpp-canvas-label {
      font-family: var(--cpp-clabel-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-clabel-size, 10px);
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--cpp-clabel-color, #8b7355);
      margin-bottom: 12px;
      font-weight: 500;
    }
    .cpp-canvas {
      position: relative;
      width: 100%;
      aspect-ratio: 1;
      border: 1px dashed var(--cpp-canvas-border, #c4b89a);
      border-radius: 2px;
      overflow: visible;
      cursor: default;
      user-select: none;
    }
    .cpp-canvas-bg {
      position: absolute;
      inset: 0;
      border-radius: 2px;
      background: repeating-conic-gradient(#e8e0d0 0% 25%, #f5f0e8 0% 50%) 0 0 / 20px 20px;
      overflow: hidden;
      pointer-events: none;
    }
    .cpp-canvas.has-product {
      border-style: solid;
      border-color: var(--cpp-chrome-border, #d4c9b0);
    }
    .cpp-canvas.has-product .cpp-canvas-bg {
      background: #fff;
    }
    .cpp-empty-hint {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      pointer-events: none;
    }
    .cpp-empty-hint svg { opacity: 0.2; }
    .cpp-empty-hint p {
      font-family: 'DM Sans', sans-serif;
      font-size: var(--cpp-empty-hint-size, 13px);
      color: var(--cpp-empty-hint-color, #8b7355);
      opacity: 0.6;
      text-align: center;
      max-width: 200px;
      line-height: 1.5;
    }
    .cpp-product-layer {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .cpp-product-layer img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
      user-select: none;
      -webkit-user-drag: none;
    }
    .cpp-artwork-layer {
      position: absolute;
      cursor: grab;
      touch-action: none;
    }
    .cpp-artwork-layer:active { cursor: grabbing; }
    .cpp-artwork-layer.selected {
      outline: 2px dashed #8b7355;
      outline-offset: 3px;
    }
    .cpp-artwork-layer img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      user-select: none;
      -webkit-user-drag: none;
      pointer-events: none;
    }
    .cpp-artwork-layer img {
      filter: var(--cpp-artwork-filter, none);
    }
    .cpp-corner-handle {
      position: absolute;
      width: 22px;
      height: 22px;
      background: #8b7355;
      border: 3px solid #fff;
      border-radius: 50%;
      z-index: 30;
      touch-action: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.5);
      transition: background 0.15s, transform 0.15s;
      margin-left: -11px;
      margin-top: -11px;
    }
    .cpp-corner-handle:hover {
      background: #5c4a30;
      transform: scale(1.2);
    }
    .cpp-corner-handle[data-corner="nw"] { cursor: nw-resize; }
    .cpp-corner-handle[data-corner="ne"] { cursor: ne-resize; }
    .cpp-corner-handle[data-corner="sw"] { cursor: sw-resize; }
    .cpp-corner-handle[data-corner="se"] { cursor: se-resize; }
    .cpp-canvas-hint {
      font-family: var(--cpp-hint-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-hint-size, 11px);
      color: var(--cpp-hint-color, #8b7355);
      text-align: center;
      margin-top: 8px;
      opacity: 0.7;
    }

    /* ── Sidebar ── */
    .cpp-sidebar {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .cpp-panel {
      background: #fff;
      border: 1px solid var(--cpp-chrome-border, #d4c9b0);
      border-radius: 4px;
      overflow: hidden;
    }
    .cpp-panel-header {
      padding: 12px 16px;
      background: var(--cpp-panel-header-bg, #faf7f0);
      border-bottom: 1px solid var(--cpp-chrome-border, #d4c9b0);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .cpp-panel-header span {
      font-family: var(--cpp-panel-header-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-panel-header-size, 10px);
      letter-spacing: 0.18em;
      text-transform: uppercase;
      font-weight: 500;
      color: var(--cpp-panel-header-text, #5c4a30);
    }
    .cpp-step-badge {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--cpp-badge-bg, #8b7355);
      color: var(--cpp-badge-text, #faf7f0);
      font-size: 11px;
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-family: 'DM Sans', sans-serif;
      transition: background 0.2s;
    }
    .cpp-step-badge.done { background: var(--cpp-badge-done-bg, #4a7c59); }
    .cpp-step-badge--adjust {
      background: var(--cpp-badge-adjust-bg, var(--cpp-badge-done-bg, #4a7c59));
      color: var(--cpp-badge-adjust-text, var(--cpp-badge-text, #faf7f0));
    }
    .cpp-panel-body { padding: 16px; }

    /* ── Product selector grid (replaces upload zone) ── */
    .cpp-product-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
      gap: 8px;
    }
    .cpp-product-option {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      cursor: pointer;
      border-radius: 4px;
      padding: 6px 4px;
      border: 2px solid transparent;
      transition: border-color 0.15s, background 0.15s;
      background: #fdfaf5;
    }
    .cpp-product-option:hover {
      background: #f5f0e8;
      border-color: var(--cpp-canvas-border, #c4b89a);
    }
    .cpp-product-option.active {
      border-color: var(--cpp-accent-border, #8b7355);
      background: #f5f0e8;
    }
    .cpp-product-option img {
      width: 100%;
      aspect-ratio: 1;
      object-fit: contain;
      border-radius: 3px;
      border: 1px solid var(--cpp-chrome-border, #e8dfd0);
      display: block;
    }
    .cpp-product-option-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px;
      color: #5c4a30;
      text-align: center;
      line-height: 1.3;
      font-weight: 500;
    }
    .cpp-product-option.active .cpp-product-option-label {
      color: #1a1208;
    }
    .cpp-no-products {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      color: #8b7355;
      text-align: center;
      padding: 12px 0;
      opacity: 0.7;
    }

    /* Upload zones */
    .cpp-upload-zone {
      border: 1.5px dashed var(--cpp-canvas-border, #c4b89a);
      border-radius: 4px;
      padding: 18px 14px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s;
      background: #fdfaf5;
      display: block;
    }
    .cpp-upload-zone:hover {
      border-color: var(--cpp-accent-border, #8b7355);
      background: #f5f0e8;
    }
    .cpp-upload-zone.disabled {
      opacity: 0.45;
      pointer-events: none;
      cursor: not-allowed;
    }
    .cpp-upload-zone input { display: none; }
    .cpp-upload-zone p {
      font-family: 'DM Sans', sans-serif;
      font-size: var(--cpp-upload-hint-size, 12px);
      color: var(--cpp-upload-hint-color, #8b7355);
      margin-top: 8px;
      line-height: 1.5;
    }
    .cpp-upload-zone p strong {
      display: block;
      font-weight: 500;
      font-size: var(--cpp-upload-title-size, 12px);
      color: var(--cpp-upload-title-color, #3d2e1a);
    }
    .cpp-thumb-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 11px;
      background: #f5f0e8;
      border-radius: 4px;
      border: 1px solid var(--cpp-chrome-border, #d4c9b0);
    }
    .cpp-thumb-row img {
      width: 38px;
      height: 38px;
      object-fit: cover;
      border-radius: 3px;
      border: 1px solid var(--cpp-canvas-border, #c4b89a);
    }
    .cpp-thumb-name {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      color: #3d2e1a;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .cpp-remove-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: #8b7355;
      font-size: 18px;
      line-height: 1;
      padding: 2px 5px;
      border-radius: 3px;
      transition: background 0.15s;
    }
    .cpp-remove-btn:hover { background: #e8dfd0; }

    /* Controls */
    .cpp-control-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;
    }
    .cpp-control-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 11px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #5c4a30;
      font-weight: 500;
    }
    .cpp-control-value {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      color: #8b7355;
    }
    .cpp-range {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 3px;
      background: #d4c9b0;
      border-radius: 2px;
      outline: none;
      margin-bottom: 16px;
    }
    .cpp-range::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #8b7355;
      cursor: pointer;
      border: 2px solid #faf7f0;
      box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    }
    .cpp-flip-row {
      display: flex;
      gap: 8px;
      margin-top: 4px;
    }
    .cpp-flip-btn {
      flex: 1;
      padding: 7px 4px;
      font-family: 'DM Sans', sans-serif;
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      background: #f5f0e8;
      border: 1px solid var(--cpp-chrome-border, #d4c9b0);
      border-radius: 3px;
      cursor: pointer;
      color: #5c4a30;
      font-weight: 500;
      transition: all 0.15s;
    }
    .cpp-flip-btn:hover { background: #e8dfd0; border-color: var(--cpp-accent-border, #8b7355); }
    .cpp-flip-btn.active { background: #e8dfd0; border-color: var(--cpp-accent-border, #8b7355); color: #3d2e1a; }

    /* CTA */
    .cpp-cta {
      background: #1a1208;
      border-radius: 4px;
      padding: 22px 18px;
      text-align: center;
    }
    .cpp-cta h3 {
      font-family: 'Playfair Display', serif !important;
      font-size: 18px !important;
      font-weight: 400 !important;
      color: #faf7f0;
      margin: 0 0 8px !important;
    }
    .cpp-cta p {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      color: #a08c6e;
      line-height: 1.6;
      margin: 0 0 18px;
    }
    .cpp-cta-btn {
      display: block;
      width: 100%;
      padding: 13px;
      border-radius: 3px;
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s;
      text-decoration: none !important;
      text-align: center;
      margin-bottom: 8px;
    }
    .cpp-cta-btn.primary {
      background: #8b7355;
      color: #faf7f0 !important;
      border: none;
    }
    .cpp-cta-btn.primary:hover { background: #a08c6e; }
    .cpp-cta-btn.secondary {
      background: transparent;
      color: #a08c6e !important;
      border: 1px solid #3d2e1a;
    }
    .cpp-cta-btn.secondary:hover { background: #2a1f0f; }
    .cpp-reset-row {
      text-align: center;
      margin-top: 8px;
    }
    .cpp-reset-link {
      font-family: var(--cpp-reset-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-reset-size, 11px);
      color: var(--cpp-reset-color, #8b7355);
      cursor: pointer;
      text-decoration: underline;
      background: none;
      border: none;
    }
    .cpp-reset-link:hover { filter: brightness(0.75); }

    /* Toast */
    .cpp-save-toast {
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%) translateY(20px);
      background: #1a1208;
      color: #faf7f0;
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      padding: 10px 22px;
      border-radius: 30px;
      opacity: 0;
      transition: opacity 0.3s, transform 0.3s;
      pointer-events: none;
      z-index: 9999;
      white-space: nowrap;
    }
    .cpp-save-toast.show {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }

    /* ── Order form (replaces CTA panel) ── */
    .cpp-order-panel {
      background: var(--cpp-panel-bg, #1a1208);
      border: var(--cpp-order-panel-border, none);
      border-radius: 4px;
      padding: 20px 18px;
    }
    .cpp-order-panel h3 {
      font-family: var(--cpp-heading-font, 'Playfair Display', serif) !important;
      font-size: var(--cpp-heading-size, 17px) !important;
      font-weight: 400 !important;
      color: var(--cpp-panel-text, #faf7f0);
      margin: 0 0 6px !important;
    }
    .cpp-order-panel > p {
      font-family: var(--cpp-body-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-body-size, 12px);
      color: var(--cpp-panel-text, #faf7f0);
      opacity: 0.7;
      line-height: 1.6;
      margin: 0 0 14px;
    }
    .cpp-field {
      margin-bottom: 10px;
    }
    .cpp-field label {
      display: block;
      font-family: var(--cpp-label-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-label-size, 10px);
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--cpp-label-color, #a08c6e);
      margin-bottom: 4px;
      font-weight: 500;
    }
    .cpp-field input,
    .cpp-field textarea {
      width: 100%;
      background: var(--cpp-field-bg, #2a1f0f);
      border: 1px solid var(--cpp-field-border, #3d2e1a);
      border-radius: 3px;
      padding: 9px 11px;
      font-family: var(--cpp-field-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-field-size, 13px);
      color: var(--cpp-field-text, #faf7f0);
      outline: none;
      transition: border-color 0.15s;
      box-sizing: border-box;
    }
    .cpp-field input::placeholder,
    .cpp-field textarea::placeholder { color: var(--cpp-placeholder-color, #a08c6e); opacity: 0.6; }
    .cpp-field input:focus,
    .cpp-field textarea:focus { border-color: var(--cpp-field-border, #3d2e1a); filter: brightness(1.4); }
    .cpp-field textarea { resize: vertical; min-height: 72px; }
    .cpp-submit-btn {
      display: block;
      width: 100%;
      padding: 13px;
      border-radius: 3px;
      font-family: var(--cpp-btn-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-btn-size, 12px);
      letter-spacing: 0.15em;
      text-transform: uppercase;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s, opacity 0.2s;
      text-align: center;
      margin-bottom: 8px;
      border: none;
      background: var(--cpp-btn-bg, #8b7355);
      color: var(--cpp-btn-text, #faf7f0);
    }
    .cpp-submit-btn:hover:not(:disabled) { filter: brightness(1.15); }
    .cpp-submit-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      position: relative;
    }
    .cpp-submit-btn[data-reason="no-artwork"]::after {
      content: 'Upload your artwork first';
      position: absolute;
      bottom: calc(100% + 6px);
      left: 50%;
      transform: translateX(-50%);
      background: #3d2e1a;
      color: #faf7f0;
      font-size: 11px;
      font-family: var(--cpp-body-font, 'DM Sans', sans-serif);
      white-space: nowrap;
      padding: 5px 10px;
      border-radius: 3px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .cpp-submit-btn[data-reason="no-artwork"]:hover::after { opacity: 1; }
    .cpp-download-btn {
      display: block;
      width: 100%;
      padding: 10px;
      border-radius: 3px;
      font-family: var(--cpp-btn-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-btn-size, 12px);
      letter-spacing: 0.13em;
      text-transform: uppercase;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s, opacity 0.2s;
      text-align: center;
      background: var(--cpp-dl-btn-bg, transparent);
      color: var(--cpp-dl-btn-text, #a08c6e);
      border: 1px solid var(--cpp-dl-btn-border, #a08c6e);
    }
    .cpp-download-btn:hover:not(:disabled) { filter: brightness(1.15); }
    .cpp-download-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .cpp-form-status {
      font-family: var(--cpp-body-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-body-size, 12px);
      text-align: center;
      margin-top: 10px;
      line-height: 1.5;
      min-height: 18px;
    }
    .cpp-form-status.error { color: #c0755a; }
    .cpp-form-status.success { color: #6aaa7e; }
    .cpp-form-status.info { color: var(--cpp-status-info-color, #a08c6e); }
    .cpp-terms {
      font-family: var(--cpp-body-font, 'DM Sans', sans-serif);
      font-size: 10px;
      color: var(--cpp-label-color, #a08c6e);
      line-height: 1.5;
      margin-bottom: 10px;
    }
    .cpp-terms a {
      color: var(--cpp-label-color, #a08c6e);
      text-decoration: underline;
    }
    .cpp-terms a:hover { opacity: 0.75; }
    .cpp-auth-note {
      font-family: var(--cpp-body-font, 'DM Sans', sans-serif);
      font-size: var(--cpp-body-size, 12px);
      color: var(--cpp-label-color, #a08c6e);
      text-align: center;
      margin-top: 8px;
      line-height: 1.5;
    }
  `;

  /* ─────────────────────────────────────────────
     SVG Icons
  ───────────────────────────────────────────── */
  const ICON_UPLOAD = `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 5v14M7 12l7-7 7 7" stroke="#8b7355" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M4 22h20" stroke="#8b7355" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`;

  const ICON_EMPTY = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="36" height="28" rx="3" stroke="#8b7355" stroke-width="2"/>
    <circle cx="17" cy="20" r="4" stroke="#8b7355" stroke-width="2"/>
    <path d="M6 32l10-8 8 6 6-5 12 9" stroke="#8b7355" stroke-width="2" stroke-linejoin="round"/>
  </svg>`;

  /* ─────────────────────────────────────────────
     State
  ───────────────────────────────────────────── */
  let state = {
    products: [],          // [{url, label}, ...]  from theme settings
    selectedProductIdx: -1,
    productSrc: null,
    artworkSrc: null,
    artworkName: '',
    posX: 0.3,
    posY: 0.25,
    size: 0.35,
    opacity: 40,
    flipH: false,
    flipV: false,
    rotation: 0,
    selected: false,
    panel1Collapsed: false, // true on mobile after a product is selected
    panel2Collapsed: false, // true on mobile after artwork is uploaded
    panel3Collapsed: false,  // starts expanded on mobile, toggled by header tap
  };

  let refs = {};

  /* ─────────────────────────────────────────────
     Init
  ───────────────────────────────────────────── */
  function init() {
    const root = document.getElementById('custom-product-preview');
    if (!root) return;

    if (!document.getElementById('cpp-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'cpp-styles';
      styleEl.textContent = CSS;
      document.head.appendChild(styleEl);
    }

    // Parse products JSON from data attribute
    let products = [];
    try {
      const raw = root.dataset.products;
      if (raw) products = JSON.parse(raw);
    } catch (e) {
      console.warn('CPP: could not parse product data', e);
    }

    // Read style config from separate config file (falls back to defaults)
    var SC = window.CPP_STYLE_CONFIG || {};

    var config = {
      artworkLabel:          root.dataset.artworkLabel          || 'Upload Your Artwork',
      heading:               decodeAttr(root.dataset.heading)               || 'See Your Design Come to Life',
      subheading:            decodeAttr(root.dataset.subheading)            || 'Choose a product, upload your artwork, and see how it looks.',
      allowDownload:         root.dataset.allowDownload         !== 'false',
      workerUrl:             root.dataset.workerUrl             || '',
      storeName:             root.dataset.storeName             || 'Custom Order',
      panelBg:               root.dataset.panelBg               || '#1a1208',
      orderPanelBorderColor: (root.dataset.orderPanelBorderColor !== undefined ? root.dataset.orderPanelBorderColor : (SC.order_panel_border_color !== undefined ? String(SC.order_panel_border_color) : undefined)) || '',
      panelText:             root.dataset.panelText             || '#faf7f0',
      labelColor:            (root.dataset.labelColor !== undefined ? root.dataset.labelColor : (SC.label_color !== undefined ? String(SC.label_color) : undefined))            || '#a08c6e',
      fieldBg:               (root.dataset.fieldBg     !== undefined ? root.dataset.fieldBg     : (SC.field_bg     !== undefined ? String(SC.field_bg)     : undefined)) || '#2a1f0f',
      fieldText:             (root.dataset.fieldText   !== undefined ? root.dataset.fieldText   : (SC.field_text   !== undefined ? String(SC.field_text)   : undefined)) || '#faf7f0',
      fieldBorder:           (root.dataset.fieldBorder !== undefined ? root.dataset.fieldBorder : (SC.field_border !== undefined ? String(SC.field_border) : undefined)) || '#3d2e1a',
      statusInfoColor:       (root.dataset.statusInfoColor !== undefined ? root.dataset.statusInfoColor : (SC.status_info_color !== undefined ? String(SC.status_info_color) : undefined)) || '#a08c6e',
      placeholderColor:      (root.dataset.placeholderColor !== undefined ? root.dataset.placeholderColor : (SC.placeholder_color !== undefined ? String(SC.placeholder_color) : undefined))      || '#a08c6e',
      artworkSepia:          (root.dataset.artworkSepia !== undefined ? root.dataset.artworkSepia : (SC.artwork_sepia !== undefined ? String(SC.artwork_sepia) : undefined))          || '0',
      artworkHue:            (root.dataset.artworkHue !== undefined ? root.dataset.artworkHue : (SC.artwork_hue !== undefined ? String(SC.artwork_hue) : undefined))            || '0',
      artworkSaturate:       (root.dataset.artworkSaturate !== undefined ? root.dataset.artworkSaturate : (SC.artwork_saturate !== undefined ? String(SC.artwork_saturate) : undefined))       || '100',
      artworkBrightness:     (root.dataset.artworkBrightness !== undefined ? root.dataset.artworkBrightness : (SC.artwork_brightness !== undefined ? String(SC.artwork_brightness) : undefined))     || '100',
      btnBg:                 root.dataset.btnBg                 || '#8b7355',
      btnText:               root.dataset.btnText               || '#faf7f0',
      dlBtnBg:               (root.dataset.dlBtnBg     !== undefined ? root.dataset.dlBtnBg     : (SC.dl_btn_bg     !== undefined ? String(SC.dl_btn_bg)     : undefined)) || 'transparent',
      dlBtnText:             (root.dataset.dlBtnText   !== undefined ? root.dataset.dlBtnText   : (SC.dl_btn_text   !== undefined ? String(SC.dl_btn_text)   : undefined)) || '#a08c6e',
      dlBtnBorder:           (root.dataset.dlBtnBorder !== undefined ? root.dataset.dlBtnBorder : (SC.dl_btn_border !== undefined ? String(SC.dl_btn_border) : undefined)) || '#a08c6e',
      canvasLabelColor:      (root.dataset.canvasLabelColor !== undefined ? root.dataset.canvasLabelColor : (SC.canvas_label_color !== undefined ? String(SC.canvas_label_color) : undefined))      || '#8b7355',
      canvasLabelFont:       (root.dataset.canvasLabelFont !== undefined ? root.dataset.canvasLabelFont : (SC.canvas_label_font !== undefined ? String(SC.canvas_label_font) : undefined))       || 'DM Sans',
      canvasLabelSize:       (root.dataset.canvasLabelSize !== undefined ? root.dataset.canvasLabelSize : (SC.canvas_label_size !== undefined ? String(SC.canvas_label_size) : undefined))       || '10',
      resetColor:            (root.dataset.resetColor !== undefined ? root.dataset.resetColor : (SC.reset_color !== undefined ? String(SC.reset_color) : undefined))            || '#8b7355',
      resetFont:             (root.dataset.resetFont !== undefined ? root.dataset.resetFont : (SC.reset_font !== undefined ? String(SC.reset_font) : undefined))             || 'DM Sans',
      resetSize:             (root.dataset.resetSize !== undefined ? root.dataset.resetSize : (SC.reset_size !== undefined ? String(SC.reset_size) : undefined))             || '11',
      chromeBorder:          (root.dataset.chromeBorder !== undefined ? root.dataset.chromeBorder : (SC.chrome_border !== undefined ? String(SC.chrome_border) : undefined)) || '#d4c9b0',
      canvasBorder:          (root.dataset.canvasBorder !== undefined ? root.dataset.canvasBorder : (SC.canvas_border !== undefined ? String(SC.canvas_border) : undefined)) || '#c4b89a',
      accentBorder:          (root.dataset.accentBorder !== undefined ? root.dataset.accentBorder : (SC.accent_border !== undefined ? String(SC.accent_border) : undefined)) || '#8b7355',
      pageHeadingFont:       (root.dataset.pageHeadingFont !== undefined ? root.dataset.pageHeadingFont : (SC.page_heading_font !== undefined ? String(SC.page_heading_font) : undefined))       || 'Playfair Display',
      pageHeadingSize:       (root.dataset.pageHeadingSize !== undefined ? root.dataset.pageHeadingSize : (SC.page_heading_size !== undefined ? String(SC.page_heading_size) : undefined))       || '',
      subheadingFont:        (root.dataset.subheadingFont !== undefined ? root.dataset.subheadingFont : (SC.subheading_font !== undefined ? String(SC.subheading_font) : undefined))        || 'DM Sans',
      subheadingSize:        (root.dataset.subheadingSize !== undefined ? root.dataset.subheadingSize : (SC.subheading_size !== undefined ? String(SC.subheading_size) : undefined))        || '14',
      panelHeaderBg:         (root.dataset.panelHeaderBg !== undefined ? root.dataset.panelHeaderBg : (SC.panel_header_bg !== undefined ? String(SC.panel_header_bg) : undefined))         || '#faf7f0',
      badgeBg:               (root.dataset.badgeBg !== undefined ? root.dataset.badgeBg : (SC.badge_bg !== undefined ? String(SC.badge_bg) : undefined))               || '#8b7355',
      badgeText:             (root.dataset.badgeText !== undefined ? root.dataset.badgeText : (SC.badge_text !== undefined ? String(SC.badge_text) : undefined))             || '#faf7f0',
      badgeDoneBg:           (root.dataset.badgeDoneBg !== undefined ? root.dataset.badgeDoneBg : (SC.badge_done_bg !== undefined ? String(SC.badge_done_bg) : undefined))           || '#4a7c59',
      badgeAdjustBg:         (root.dataset.badgeAdjustBg !== undefined ? root.dataset.badgeAdjustBg : (SC.badge_adjust_bg !== undefined ? String(SC.badge_adjust_bg) : undefined))         || '',
      badgeAdjustText:       (root.dataset.badgeAdjustText !== undefined ? root.dataset.badgeAdjustText : (SC.badge_adjust_text !== undefined ? String(SC.badge_adjust_text) : undefined))       || '',
      panelHeaderText:       (root.dataset.panelHeaderText !== undefined ? root.dataset.panelHeaderText : (SC.panel_header_text !== undefined ? String(SC.panel_header_text) : undefined))       || '#5c4a30',
      panelHeaderFont:       (root.dataset.panelHeaderFont !== undefined ? root.dataset.panelHeaderFont : (SC.panel_header_font !== undefined ? String(SC.panel_header_font) : undefined))       || 'DM Sans',
      panelHeaderSize:       (root.dataset.panelHeaderSize !== undefined ? root.dataset.panelHeaderSize : (SC.panel_header_size !== undefined ? String(SC.panel_header_size) : undefined))       || '10',
      artworkFilter:         root.dataset.artworkFilter         || '',
      canvasLabel:           decodeAttr(root.dataset.canvasLabel)           || 'Preview Canvas',
      canvasHintText:        decodeAttr((root.dataset.canvasHintText !== undefined ? root.dataset.canvasHintText : (SC.canvas_hint_text !== undefined ? String(SC.canvas_hint_text) : undefined))) || 'Drag to reposition · Drag corner handle to resize',
      canvasHintColor:       (root.dataset.canvasHintColor !== undefined ? root.dataset.canvasHintColor : (SC.canvas_hint_color !== undefined ? String(SC.canvas_hint_color) : undefined))       || '#8b7355',
      canvasHintFont:        (root.dataset.canvasHintFont !== undefined ? root.dataset.canvasHintFont : (SC.canvas_hint_font !== undefined ? String(SC.canvas_hint_font) : undefined))        || 'DM Sans',
      canvasHintSize:        (root.dataset.canvasHintSize !== undefined ? root.dataset.canvasHintSize : (SC.canvas_hint_size !== undefined ? String(SC.canvas_hint_size) : undefined))        || '11',
      emptyHintColor:        (root.dataset.emptyHintColor !== undefined ? root.dataset.emptyHintColor : (SC.empty_hint_color !== undefined ? String(SC.empty_hint_color) : undefined))        || '#8b7355',
      emptyHintSize:         (root.dataset.emptyHintSize !== undefined ? root.dataset.emptyHintSize : (SC.empty_hint_size !== undefined ? String(SC.empty_hint_size) : undefined))         || '13',
      uploadTitleColor:      (root.dataset.uploadTitleColor !== undefined ? root.dataset.uploadTitleColor : (SC.upload_title_color !== undefined ? String(SC.upload_title_color) : undefined))      || '#3d2e1a',
      uploadTitleSize:       (root.dataset.uploadTitleSize !== undefined ? root.dataset.uploadTitleSize : (SC.upload_title_size !== undefined ? String(SC.upload_title_size) : undefined))       || '12',
      uploadHintColor:       (root.dataset.uploadHintColor !== undefined ? root.dataset.uploadHintColor : (SC.upload_hint_color !== undefined ? String(SC.upload_hint_color) : undefined))       || '#8b7355',
      uploadHintSize:        (root.dataset.uploadHintSize !== undefined ? root.dataset.uploadHintSize : (SC.upload_hint_size !== undefined ? String(SC.upload_hint_size) : undefined))        || '12',
      uploadTitle:           decodeAttr(root.dataset.uploadTitle)           || 'Upload Your Design',
      uploadHint:            decodeAttr(root.dataset.uploadHint)            || 'PNG with transparent background recommended',
      uploadDisabledHint:    decodeAttr(root.dataset.uploadDisabledHint)    || 'Choose a product first',
      successMessage:        decodeAttr(root.dataset.successMessage)        || 'Image submitted! We will be in touch shortly.',
      successColor:          root.dataset.successColor          || '#6aaa7e',
      termsText:             SC.terms_text          || '',
      termsUrl:              SC.terms_url           || '',
      termsLinkText:         SC.terms_link_text     || 'View our terms and conditions.',
      privacyUrl:            SC.privacy_url         || '',
      privacyLinkText:       SC.privacy_link_text   || 'Privacy policy.',
      termsSeparator:        SC.terms_separator     || '',
      messagePlaceholder:    decodeAttr(root.dataset.messagePlaceholder) || SC.message_placeholder || 'Any details about your order, colours, sizing…',
      panelTitle:            decodeAttr(root.dataset.panelTitle)            || 'Submit a Custom Image',
      panelDesc:             decodeAttr(root.dataset.panelDesc)             || "Happy with your preview? Fill in your details and we'll be in touch to make it happen.",
      headingFont:           (root.dataset.headingFont !== undefined ? root.dataset.headingFont : (SC.heading_font !== undefined ? String(SC.heading_font) : undefined))           || 'Playfair Display',
      headingSize:           (root.dataset.headingSize !== undefined ? root.dataset.headingSize : (SC.heading_size !== undefined ? String(SC.heading_size) : undefined))           || '17',
      bodyFont:              (root.dataset.bodyFont !== undefined ? root.dataset.bodyFont : (SC.body_font !== undefined ? String(SC.body_font) : undefined))              || 'DM Sans',
      bodySize:              (root.dataset.bodySize !== undefined ? root.dataset.bodySize : (SC.body_size !== undefined ? String(SC.body_size) : undefined))              || '12',
      labelFont:             (root.dataset.labelFont !== undefined ? root.dataset.labelFont : (SC.label_font !== undefined ? String(SC.label_font) : undefined))             || 'DM Sans',
      labelSize:             (root.dataset.labelSize !== undefined ? root.dataset.labelSize : (SC.label_size !== undefined ? String(SC.label_size) : undefined))             || '10',
      fieldFont:             (root.dataset.fieldFont !== undefined ? root.dataset.fieldFont : (SC.field_font !== undefined ? String(SC.field_font) : undefined))             || 'DM Sans',
      fieldSize:             (root.dataset.fieldSize !== undefined ? root.dataset.fieldSize : (SC.field_size !== undefined ? String(SC.field_size) : undefined))             || '13',
      btnFont:               (root.dataset.btnFont !== undefined ? root.dataset.btnFont : (SC.btn_font !== undefined ? String(SC.btn_font) : undefined))               || 'DM Sans',
      btnSize:               (root.dataset.btnSize !== undefined ? root.dataset.btnSize : (SC.btn_size !== undefined ? String(SC.btn_size) : undefined))               || '12',
      products,
    };

    state.products = products;

    // Auto-select first product if only one is configured
    if (products.length === 1) {
      state.selectedProductIdx = 0;
      state.productSrc = products[0].url;
    }

    root.innerHTML = buildHTML(config);
    bindRefs(root);
    bindEvents(config);
    // Canvas label and reset link live outside the order panel — apply to root
    // Page heading font and size
    root.style.setProperty('--cpp-page-heading-font', "'" + config.pageHeadingFont + "', serif");
    if (config.pageHeadingSize) {
      root.style.setProperty('--cpp-page-heading-size', config.pageHeadingSize + 'px');
    }
    // Subheading font and size
    root.style.setProperty('--cpp-subheading-font',    "'" + config.subheadingFont + "', sans-serif");
    root.style.setProperty('--cpp-subheading-size',    config.subheadingSize + 'px');
    // Panel header background, text, font, size
    root.style.setProperty('--cpp-panel-header-bg',   config.panelHeaderBg);
    root.style.setProperty('--cpp-badge-bg',          config.badgeBg);
    root.style.setProperty('--cpp-badge-text',        config.badgeText);
    root.style.setProperty('--cpp-badge-done-bg',     config.badgeDoneBg);
    if (config.badgeAdjustBg)   root.style.setProperty('--cpp-badge-adjust-bg',   config.badgeAdjustBg);
    if (config.badgeAdjustText) root.style.setProperty('--cpp-badge-adjust-text', config.badgeAdjustText);
    root.style.setProperty('--cpp-panel-header-text', config.panelHeaderText);
    root.style.setProperty('--cpp-panel-header-font', "'" + config.panelHeaderFont + "', sans-serif");
    root.style.setProperty('--cpp-panel-header-size', config.panelHeaderSize + 'px');
    // Artwork tint — applied to the canvas element so it affects the artwork layer
    // Build CSS filter string for artwork image from individual settings
    root.style.setProperty('--cpp-artwork-filter', buildArtworkFilter(config));
    root.style.setProperty('--cpp-empty-hint-color',  config.emptyHintColor);
    root.style.setProperty('--cpp-empty-hint-size',   config.emptyHintSize + 'px');
    root.style.setProperty('--cpp-upload-title-color',config.uploadTitleColor);
    root.style.setProperty('--cpp-upload-title-size', config.uploadTitleSize + 'px');
    root.style.setProperty('--cpp-upload-hint-color', config.uploadHintColor);
    root.style.setProperty('--cpp-upload-hint-size',  config.uploadHintSize + 'px');
    root.style.setProperty('--cpp-hint-color',     config.canvasHintColor);
    root.style.setProperty('--cpp-hint-font',      "'" + config.canvasHintFont + "', sans-serif");
    root.style.setProperty('--cpp-hint-size',      config.canvasHintSize + 'px');
    root.style.setProperty('--cpp-clabel-color',  config.canvasLabelColor);
    root.style.setProperty('--cpp-clabel-font',   "'" + config.canvasLabelFont + "', sans-serif");
    root.style.setProperty('--cpp-clabel-size',   config.canvasLabelSize + 'px');
    root.style.setProperty('--cpp-reset-color',   config.resetColor);
    root.style.setProperty('--cpp-reset-font',    "'" + config.resetFont + "', sans-serif");
    root.style.setProperty('--cpp-reset-size',    config.resetSize + 'px');
    root.style.setProperty('--cpp-chrome-border', config.chromeBorder);
    root.style.setProperty('--cpp-canvas-border', config.canvasBorder);
    root.style.setProperty('--cpp-accent-border', config.accentBorder);
    // Apply all panel/field colours and fonts as CSS custom properties
    var p = refs.orderPanel;
    p.style.setProperty('--cpp-panel-bg',          config.panelBg);
    p.style.setProperty('--cpp-order-panel-border',
      config.orderPanelBorderColor ? '1px solid ' + config.orderPanelBorderColor : 'none');
    p.style.setProperty('--cpp-panel-text',         config.panelText);
    p.style.setProperty('--cpp-label-color',        config.labelColor);
    p.style.setProperty('--cpp-field-bg',           config.fieldBg);
    p.style.setProperty('--cpp-field-text',         config.fieldText);
    p.style.setProperty('--cpp-field-border',       config.fieldBorder);
    p.style.setProperty('--cpp-status-info-color',  config.statusInfoColor);
    p.style.setProperty('--cpp-placeholder-color',  config.placeholderColor);
    p.style.setProperty('--cpp-btn-bg',             config.btnBg);
    p.style.setProperty('--cpp-btn-text',           config.btnText);
    p.style.setProperty('--cpp-dl-btn-bg',          config.dlBtnBg);
    p.style.setProperty('--cpp-dl-btn-text',        config.dlBtnText);
    p.style.setProperty('--cpp-dl-btn-border',      config.dlBtnBorder);
    p.style.setProperty('--cpp-heading-font',       "'" + config.headingFont + "', serif");
    p.style.setProperty('--cpp-heading-size',       config.headingSize + 'px');
    p.style.setProperty('--cpp-body-font',          "'" + config.bodyFont + "', sans-serif");
    p.style.setProperty('--cpp-body-size',          config.bodySize + 'px');
    p.style.setProperty('--cpp-label-font',         "'" + config.labelFont + "', sans-serif");
    p.style.setProperty('--cpp-label-size',         config.labelSize + 'px');
    p.style.setProperty('--cpp-field-font',         "'" + config.fieldFont + "', sans-serif");
    p.style.setProperty('--cpp-field-size',         config.fieldSize + 'px');
    p.style.setProperty('--cpp-btn-font',           "'" + config.btnFont + "', sans-serif");
    p.style.setProperty('--cpp-btn-size',           config.btnSize + 'px');
    render();
  }

  /* ─────────────────────────────────────────────
     Build HTML skeleton
  ───────────────────────────────────────────── */
  function buildHTML(config) {
    return `
      <div class="cpp-header">
        <h2 class="cpp-heading">${escapeHtml(config.heading)}</h2>
        <p class="cpp-subheading">${escapeHtml(config.subheading)}</p>
      </div>

      <div class="cpp-layout">

        <!-- Canvas -->
        <div class="cpp-canvas-area">
          <p class="cpp-canvas-label">${escapeHtml(config.canvasLabel)}</p>
          <div class="cpp-canvas" id="cpp-canvas">
            <div class="cpp-canvas-bg" id="cpp-canvas-bg"></div>
            <div class="cpp-empty-hint" id="cpp-empty-hint">
              ${ICON_EMPTY}
              <p>Choose a product from the panel to get started</p>
            </div>
            <div class="cpp-product-layer" id="cpp-product-layer" style="display:none">
              <img id="cpp-product-img" src="" alt="Product template" draggable="false">
            </div>
            <div class="cpp-artwork-layer" id="cpp-artwork-layer" style="display:none">
              <img id="cpp-artwork-img" src="" alt="Your artwork" draggable="false">
            </div>
          </div>
          <p class="cpp-canvas-hint" id="cpp-canvas-hint" style="display:none">${escapeHtml(config.canvasHintText)}</p>
        </div>

        <!-- Sidebar -->
        <div class="cpp-sidebar">

          <!-- Step 1: product selector -->
          <div class="cpp-panel cpp-panel--collapsible" id="cpp-panel-1">
            <div class="cpp-panel-header" id="cpp-panel-1-header">
              <div class="cpp-step-badge" id="cpp-badge-1">1</div>
              <span>Choose a Product</span>
              <span class="cpp-panel-chevron">▼</span>
            </div>
            <div class="cpp-panel-body" id="cpp-panel-1-body"></div>
          </div>

          <!-- Step 2: artwork upload -->
          <div class="cpp-panel cpp-panel--collapsible" id="cpp-panel-2">
            <div class="cpp-panel-header" id="cpp-panel-2-header">
              <div class="cpp-step-badge" id="cpp-badge-2">2</div>
              <span>Your Artwork</span>
              <span class="cpp-panel-chevron">▼</span>
            </div>
            <div class="cpp-panel-body" id="cpp-panel-2-body"></div>
          </div>

          <!-- Step 3: adjust controls -->
          <div class="cpp-panel cpp-panel--collapsible" id="cpp-controls-panel" style="display:none">
            <div class="cpp-panel-header" id="cpp-panel-3-header">
              <div class="cpp-step-badge done cpp-step-badge--adjust">⚙</div>
              <span>Adjust Artwork</span>
              <span class="cpp-panel-chevron">▼</span>
            </div>
            <div class="cpp-panel-body" id="cpp-panel-3-body">
              <div class="cpp-control-row">
                <label class="cpp-control-label" for="cpp-size-range">Size</label>
                <span class="cpp-control-value" id="cpp-size-val">35%</span>
              </div>
              <input type="range" class="cpp-range" id="cpp-size-range" min="10" max="90" value="35" aria-describedby="cpp-size-val">

              <div class="cpp-control-row">
                <label class="cpp-control-label" for="cpp-rotation-range">Rotation</label>
                <span class="cpp-control-value" id="cpp-rotation-val">0°</span>
              </div>
              <input type="range" class="cpp-range" id="cpp-rotation-range" min="0" max="360" value="0" aria-describedby="cpp-rotation-val">

              <div class="cpp-control-row" style="margin-bottom:6px">
                <span class="cpp-control-label">Mirror / Flip</span>
              </div>
              <div class="cpp-flip-row">
                <button class="cpp-flip-btn" id="cpp-flip-h">↔ Horizontal</button>
                <button class="cpp-flip-btn" id="cpp-flip-v">↕ Vertical</button>
              </div>
            </div>
          </div>

          <!-- Order form -->
          <div class="cpp-order-panel" id="cpp-order-panel">
            <h3>${escapeHtml(config.panelTitle)}</h3>
            <p>${escapeHtml(config.panelDesc)}</p>
            <div class="cpp-field">
              <label for="cpp-name">Your Name *</label>
              <input type="text" id="cpp-name" placeholder="Jane Smith" autocomplete="name" required>
            </div>
            <div class="cpp-field">
              <label for="cpp-email">Email Address *</label>
              <input type="email" id="cpp-email" placeholder="jane@example.com" autocomplete="email" required>
            </div>
            <div class="cpp-field">
              <label for="cpp-message">Message *</label>
              <textarea id="cpp-message" placeholder="${escapeHtml(config.messagePlaceholder)}" required></textarea>
            </div>
            <!-- Honeypot: hidden from real users, bots fill it in -->
            <div style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;" aria-hidden="true">
              <label for="cpp-website">Website</label>
              <input type="text" id="cpp-website" name="cpp-website" tabindex="-1" autocomplete="off">
            </div>
            ${(function() {
              if (!config.termsText) return '';
              var text = escapeHtml(config.termsText);
              if (config.termsUrl && config.termsLinkText) {
                text = text + ' <a href="' + config.termsUrl + '" target="_blank" rel="noopener">' + escapeHtml(config.termsLinkText) + '</a>';
              }
              if (config.privacyUrl && config.privacyLinkText) {
                var sep = config.termsSeparator ? ' ' + escapeHtml(config.termsSeparator) + ' ' : ' ';
                text = text + sep + '<a href="' + config.privacyUrl + '" target="_blank" rel="noopener">' + escapeHtml(config.privacyLinkText) + '</a>';
              }
              return '<p class="cpp-terms">' + text + '</p>';
            })()}
            <button class="cpp-submit-btn" id="cpp-submit-btn" disabled>Submit Custom Image</button>
            ${config.allowDownload ? '<button class="cpp-download-btn" id="cpp-save-btn" disabled>⬇ Save Preview as Image</button>' : ''}
            <div class="cpp-form-status" id="cpp-form-status"></div>
            <p class="cpp-auth-note" id="cpp-auth-note"></p>
          </div>

          <div class="cpp-reset-row">
            <button class="cpp-reset-link" id="cpp-reset-btn">Reset everything</button>
          </div>

        </div>
      </div>

      <div class="cpp-save-toast" id="cpp-toast"></div>
    `;
  }

  /* ─────────────────────────────────────────────
     Bind DOM refs
  ───────────────────────────────────────────── */
  function bindRefs(root) {
    refs = {
      canvas:        root.querySelector('#cpp-canvas'),
      emptyHint:     root.querySelector('#cpp-empty-hint'),
      productLayer:  root.querySelector('#cpp-product-layer'),
      productImg:    root.querySelector('#cpp-product-img'),
      artworkLayer:  root.querySelector('#cpp-artwork-layer'),
      artworkImg:    root.querySelector('#cpp-artwork-img'),
      cornerHandles: [], // populated by createCornerHandles()
      canvasHint:    root.querySelector('#cpp-canvas-hint'),
      badge1:        root.querySelector('#cpp-badge-1'),
      badge2:        root.querySelector('#cpp-badge-2'),
      panel1:        root.querySelector('#cpp-panel-1'),
      panel1Header:  root.querySelector('#cpp-panel-1-header'),
      panel1Body:    root.querySelector('#cpp-panel-1-body'),
      panel2:        root.querySelector('#cpp-panel-2'),
      panel2Header:  root.querySelector('#cpp-panel-2-header'),
      panel2Body:    root.querySelector('#cpp-panel-2-body'),
      panel3Header:  root.querySelector('#cpp-panel-3-header'),
      panel2Body:    root.querySelector('#cpp-panel-2-body'),
      controlsPanel: root.querySelector('#cpp-controls-panel'),
      sizeRange:     root.querySelector('#cpp-size-range'),
      sizeVal:       root.querySelector('#cpp-size-val'),
      opacityRange:  null,
      opacityVal:    null,
      rotationRange: root.querySelector('#cpp-rotation-range'),
      rotationVal:   root.querySelector('#cpp-rotation-val'),
      flipH:         root.querySelector('#cpp-flip-h'),
      flipV:         root.querySelector('#cpp-flip-v'),
      orderPanel:    root.querySelector('#cpp-order-panel'),
      saveBtn:       root.querySelector('#cpp-save-btn'),
      submitBtn:     root.querySelector('#cpp-submit-btn'),
      nameInput:     root.querySelector('#cpp-name'),
      emailInput:    root.querySelector('#cpp-email'),
      messageInput:  root.querySelector('#cpp-message'),
      formStatus:    root.querySelector('#cpp-form-status'),
      honeypot:      root.querySelector('#cpp-website'),
      authNote:      root.querySelector('#cpp-auth-note'),
      resetBtn:      root.querySelector('#cpp-reset-btn'),
      toast:         root.querySelector('#cpp-toast'),
    };
  }

  /* ─────────────────────────────────────────────
     Bind events
  ───────────────────────────────────────────── */
  function bindEvents(config) {
    // Store config on refs so Dropbox/webhook functions can access it
    refs.config = config;

    // Submit order
    refs.submitBtn.addEventListener('click', function () { submitOrder(); });

    // Size
    refs.sizeRange.addEventListener('input', function () {
      state.size = this.value / 100;
      refs.sizeVal.textContent = this.value + '%';
      updateArtworkTransform();
    });
    // Flip
    refs.rotationRange.addEventListener('input', function () {
      state.rotation = parseInt(this.value);
      refs.rotationVal.textContent = this.value + '°';
      updateArtworkTransform();
    });
    refs.flipH.addEventListener('click', function () {
      state.flipH = !state.flipH;
      this.classList.toggle('active', state.flipH);
      updateArtworkTransform();
    });
    refs.flipV.addEventListener('click', function () {
      state.flipV = !state.flipV;
      this.classList.toggle('active', state.flipV);
      updateArtworkTransform();
    });
    // Artwork drag
    refs.artworkLayer.addEventListener('mousedown', onArtworkDragStart);
    refs.artworkLayer.addEventListener('touchstart', onArtworkTouchStart, { passive: false });
    // Corner handles are created/updated in updateCornerHandles()
    // Deselect
    refs.canvas.addEventListener('click', function (e) {
      if (e.target === refs.canvas || e.target.id === 'cpp-canvas-bg') {
        state.selected = false;
        refs.artworkLayer.classList.remove('selected');
        updateCornerHandles();
      }
    });
    // Save / reset
    if (refs.saveBtn) refs.saveBtn.addEventListener('click', savePreviewImage);
    refs.resetBtn.addEventListener('click', resetAll);

    // Reposition artwork whenever the canvas is resized (e.g. orientation change).
    // posX/posY/size are stored as fractions so updateArtworkTransform() always
    // produces the correct pixel values for the current canvas dimensions.
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        if (state.artworkSrc) updateArtworkTransform();
      });
      ro.observe(refs.canvas);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', function () {
        if (state.artworkSrc) updateArtworkTransform();
      });
      window.addEventListener('orientationchange', function () {
        // Small delay lets the browser finish the rotation reflow
        setTimeout(function () {
          if (state.artworkSrc) updateArtworkTransform();
        }, 100);
      });
    }
  }

  /* ─────────────────────────────────────────────
     Render
  ───────────────────────────────────────────── */
  function render() {
    renderProductPanel();
    renderArtworkPanel();
    renderCanvas();
  }

  function renderProductPanel() {
    const { products, selectedProductIdx } = state;

    if (!products.length) {
      refs.panel1Body.innerHTML = `<p class="cpp-no-products">No products have been configured yet. Add product images in the theme editor.</p>`;
      return;
    }

    // Build grid
    let html = '<div class="cpp-product-grid">';
    products.forEach(function (p, i) {
      const active = i === selectedProductIdx ? ' active' : '';
      html += `
        <div class="cpp-product-option${active}" data-idx="${i}">
          <img src="${escapeHtml(p.url)}" alt="${escapeHtml(p.label)}">
          <span class="cpp-product-option-label">${escapeHtml(p.label)}</span>
        </div>
      `;
    });
    html += '</div>';
    refs.panel1Body.innerHTML = html;

    // Bind click on each option
    refs.panel1Body.querySelectorAll('.cpp-product-option').forEach(function (el) {
      el.addEventListener('click', function () {
        const idx = parseInt(this.dataset.idx);
        state.selectedProductIdx = idx;
        state.productSrc = state.products[idx].url;
        state.selected = false;
        // Auto-collapse on mobile after selection
        if (isMobile()) state.panel1Collapsed = true;
        render();
      });
    });

    // Badge
    if (selectedProductIdx >= 0) {
      refs.badge1.textContent = '✓';
      refs.badge1.classList.add('done');
    } else {
      refs.badge1.textContent = '1';
      refs.badge1.classList.remove('done');
    }

    // Apply collapsed class on mobile only
    if (selectedProductIdx < 0) state.panel1Collapsed = false;
    if (isMobile() && state.panel1Collapsed) {
      refs.panel1.classList.add('collapsed');
    } else {
      refs.panel1.classList.remove('collapsed');
    }

    // Bind header tap to toggle — mobile only, only when a product is selected
    refs.panel1Header.onclick = null;
    if (isMobile() && selectedProductIdx >= 0) {
      refs.panel1Header.onclick = function () {
        state.panel1Collapsed = !state.panel1Collapsed;
        refs.panel1.classList.toggle('collapsed', state.panel1Collapsed);
      };
    }
  }

  function renderArtworkPanel() {
    const { productSrc, artworkSrc, artworkName } = state;
    const enabled = !!productSrc;
    const cfg = refs.config || {};

    if (artworkSrc) {
      refs.panel2Body.innerHTML = `
        <div class="cpp-thumb-row">
          <img src="${artworkSrc}" alt="">
          <span class="cpp-thumb-name">${escapeHtml(artworkName)}</span>
          <button class="cpp-remove-btn" id="cpp-remove-artwork" title="Remove">×</button>
        </div>
      `;
      document.getElementById('cpp-remove-artwork').addEventListener('click', function () {
        state.artworkSrc = null;
        state.artworkName = '';
        state.selected = false;
        state.panel2Collapsed = false;
        render();
      });
      refs.badge2.textContent = '✓';
      refs.badge2.classList.add('done');
    } else {
      const disabledClass = enabled ? '' : ' disabled';
      const uploadTitle = cfg.uploadTitle || 'Upload Your Design';
      const uploadHint  = enabled
        ? (cfg.uploadHint || 'PNG or SVG with transparent background recommended')
        : (cfg.uploadDisabledHint || 'Choose a product first');
      refs.panel2Body.innerHTML = `
        <label class="cpp-upload-zone${disabledClass}" id="cpp-artwork-zone">
          ${ICON_UPLOAD}
          <p><strong>${escapeHtml(uploadTitle)}</strong>${escapeHtml(uploadHint)}</p>
          <input type="file" accept=".png,image/png" id="cpp-artwork-input" aria-label="${escapeHtml(uploadTitle)}" ${enabled ? '' : 'disabled'}>
        </label>
      `;
      if (enabled) {
        document.getElementById('cpp-artwork-input').addEventListener('change', function (e) {
          const file = e.target.files[0];
          if (!file) return;
          state.artworkSrc = URL.createObjectURL(file);
          state.artworkName = file.name;
          state.posX = 0.3; state.posY = 0.25;
          state.size = 0.35;
          state.selected = true;
          if (isMobile()) state.panel2Collapsed = true;
          render();
        });
      }
      refs.badge2.textContent = '2';
      refs.badge2.classList.remove('done');
      // No artwork — always expanded
      state.panel2Collapsed = false;
    }

    // Apply collapsed class on mobile only
    if (isMobile() && state.panel2Collapsed) {
      refs.panel2.classList.add('collapsed');
    } else {
      refs.panel2.classList.remove('collapsed');
    }

    // Bind header tap to toggle — mobile only, only when artwork is present
    refs.panel2Header.onclick = null;
    if (isMobile() && artworkSrc) {
      refs.panel2Header.onclick = function () {
        state.panel2Collapsed = !state.panel2Collapsed;
        refs.panel2.classList.toggle('collapsed', state.panel2Collapsed);
      };
    }
  }

  function renderCanvas() {
    const { productSrc, artworkSrc } = state;

    if (productSrc) {
      refs.emptyHint.style.display = 'none';
      refs.canvas.classList.add('has-product');
      // Set canvas aspect-ratio to match the product image so it fills
      // the canvas exactly with no letterbox — artwork coordinates are then 1:1.
      refs.productImg.onload = function () {
        var iw = refs.productImg.naturalWidth;
        var ih = refs.productImg.naturalHeight;
        if (iw && ih) {
          refs.canvas.style.aspectRatio = iw + ' / ' + ih;
          // Reposition artwork now that canvas dimensions have changed
          if (state.artworkSrc) updateArtworkTransform();
        }
      };
      refs.productImg.src = productSrc;
      refs.productLayer.style.display = 'flex';
    } else {
      refs.canvas.classList.remove('has-product');
      refs.canvas.style.aspectRatio = '1';
      refs.emptyHint.style.display = 'flex';
      refs.productLayer.style.display = 'none';
      refs.artworkLayer.style.display = 'none';
      refs.canvasHint.style.display = 'none';
      refs.controlsPanel.style.display = 'none';
      return;
    }

    if (artworkSrc) {
      refs.artworkImg.src = artworkSrc;
      refs.artworkImg.style.opacity = state.opacity / 100;
      refs.artworkImg.style.filter = buildArtworkFilter(refs.config || {});
      refs.artworkLayer.style.display = 'block';
      // Artwork present — enable submission and download
      if (refs.submitBtn && !refs.submitBtn.dataset.submitted) {
        refs.submitBtn.disabled = false;
        delete refs.submitBtn.dataset.reason;
      }
      if (refs.saveBtn) refs.saveBtn.disabled = false;
      refs.artworkLayer.classList.toggle('selected', state.selected);
      refs.canvasHint.style.display = 'block';
      refs.controlsPanel.style.display = 'block';
      // Apply Step 3 collapsed state on mobile
      if (isMobile() && state.panel3Collapsed) {
        refs.controlsPanel.classList.add('collapsed');
      } else {
        refs.controlsPanel.classList.remove('collapsed');
      }
      // Bind Step 3 header tap to toggle — always available on mobile
      if (refs.panel3Header) {
        refs.panel3Header.onclick = isMobile() ? function () {
          state.panel3Collapsed = !state.panel3Collapsed;
          refs.controlsPanel.classList.toggle('collapsed', state.panel3Collapsed);
        } : null;
      }
      // Create handles if they don't exist yet.
      if (!refs.cornerHandles || !refs.cornerHandles.length) createCornerHandles();
      // Showing the controls panel causes a reflow that can shift canvas dimensions.
      // Double-rAF guarantees we read offsetWidth/offsetHeight after the browser has
      // fully recalculated layout and painted, so handle positions are always correct.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          updateArtworkTransform();
        });
      });
    } else {
      refs.artworkLayer.style.display = 'none';
      refs.canvasHint.style.display = 'none';
      refs.controlsPanel.style.display = 'none';
      // Hide all corner handles
      if (refs.cornerHandles) refs.cornerHandles.forEach(function (el) { el.style.display = 'none'; });
      // No artwork — disable submission and download
      if (refs.submitBtn && !refs.submitBtn.dataset.submitted) {
        refs.submitBtn.disabled = true;
        refs.submitBtn.dataset.reason = 'no-artwork';
      }
      if (refs.saveBtn) refs.saveBtn.disabled = true;
    }
  }

  /* ─────────────────────────────────────────────
     Artwork transform
  ───────────────────────────────────────────── */
  function updateArtworkTransform() {
    const cw = refs.canvas.offsetWidth;
    const ch = refs.canvas.offsetHeight;
    const sz = state.size * Math.min(cw, ch);

    refs.artworkLayer.style.left   = (state.posX * cw) + 'px';
    refs.artworkLayer.style.top    = (state.posY * ch) + 'px';
    refs.artworkLayer.style.width  = sz + 'px';
    refs.artworkLayer.style.height = sz + 'px';

    const transforms = [];
    if (state.rotation) transforms.push('rotate(' + state.rotation + 'deg)');
    if (state.flipH)    transforms.push('scaleX(-1)');
    if (state.flipV)    transforms.push('scaleY(-1)');
    refs.artworkImg.style.transform = transforms.join(' ');

    updateCornerHandles();
  }

  /* ─────────────────────────────────────────────
     Drag – Mouse
  ───────────────────────────────────────────── */
  function onArtworkDragStart(e) {
    if (e.target === refs.resizeHandle) return;
    e.preventDefault();
    state.selected = true;
    refs.artworkLayer.classList.add('selected');
    updateCornerHandles();

    const rect = refs.canvas.getBoundingClientRect();
    const startOffsetX = (e.clientX - rect.left) / rect.width  - state.posX;
    const startOffsetY = (e.clientY - rect.top)  / rect.height - state.posY;

    function onMove(me) {
      const r = refs.canvas.getBoundingClientRect();
      state.posX = clamp((me.clientX - r.left) / r.width  - startOffsetX, 0, 1 - state.size);
      state.posY = clamp((me.clientY - r.top)  / r.height - startOffsetY, 0, 1 - state.size);
      updateArtworkTransform();
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  /* ─────────────────────────────────────────────
     Drag – Touch
  ───────────────────────────────────────────── */
  function onArtworkTouchStart(e) {
    if (e.target === refs.resizeHandle) return;
    e.preventDefault();
    state.selected = true;
    refs.artworkLayer.classList.add('selected');
    updateCornerHandles();

    const touch = e.touches[0];
    const rect  = refs.canvas.getBoundingClientRect();
    const startOffsetX = (touch.clientX - rect.left) / rect.width  - state.posX;
    const startOffsetY = (touch.clientY - rect.top)  / rect.height - state.posY;

    function onMove(te) {
      const t = te.touches[0];
      const r = refs.canvas.getBoundingClientRect();
      state.posX = clamp((t.clientX - r.left) / r.width  - startOffsetX, 0, 1 - state.size);
      state.posY = clamp((t.clientY - r.top)  / r.height - startOffsetY, 0, 1 - state.size);
      updateArtworkTransform();
    }
    function onEnd() {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    }
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }

  /* ─────────────────────────────────────────────
     Corner Handles – created as canvas siblings
     so they are never clipped by the artwork layer
  ───────────────────────────────────────────── */
  function createCornerHandles() {
    // Remove any existing handles
    refs.canvas.querySelectorAll('.cpp-corner-handle').forEach(function (el) { el.remove(); });
    refs.cornerHandles = [];

    ['nw', 'ne', 'sw', 'se'].forEach(function (corner) {
      const el = document.createElement('div');
      el.className = 'cpp-corner-handle';
      el.dataset.corner = corner;
      el.style.display = 'none';
      refs.canvas.appendChild(el);
      refs.cornerHandles.push(el);

      el.addEventListener('mousedown', function (e) { onCornerResizeStart(e, corner); });
      el.addEventListener('touchstart', function (e) { onCornerResizeTouchStart(e, corner); }, { passive: false });
    });
  }

  function updateCornerHandles() {
    if (!refs.cornerHandles || !refs.cornerHandles.length) return;

    const cw = refs.canvas.offsetWidth;
    const ch = refs.canvas.offsetHeight;
    const sz = state.size * Math.min(cw, ch);
    const ax = state.posX * cw;
    const ay = state.posY * ch;

    // Corner pixel positions (top-left origin of each corner)
    const corners = {
      nw: { x: ax,      y: ay      },
      ne: { x: ax + sz, y: ay      },
      sw: { x: ax,      y: ay + sz },
      se: { x: ax + sz, y: ay + sz },
    };

    const show = !!state.artworkSrc && state.selected;
    refs.cornerHandles.forEach(function (el) {
      const c = corners[el.dataset.corner];
      el.style.left    = c.x + 'px';
      el.style.top     = c.y + 'px';
      el.style.display = show ? 'block' : 'none';
    });
  }

  /* Resize from any corner – Mouse */
  function onCornerResizeStart(e, corner) {
    e.preventDefault();
    e.stopPropagation();

    const rect      = refs.canvas.getBoundingClientRect();
    const startX    = e.clientX;
    const startY    = e.clientY;
    const startSize = state.size;
    const startPosX = state.posX;
    const startPosY = state.posY;
    const minDim    = Math.min(rect.width, rect.height);

    function onMove(me) {
      const dx = (me.clientX - startX) / minDim;
      const dy = (me.clientY - startY) / minDim;

      // For NW and SW corners, dragging left makes the image bigger (invert dx)
      // For NW and NE corners, dragging up makes the image bigger (invert dy)
      let delta;
      if (corner === 'se') delta =  (dx + dy) / 2;
      if (corner === 'sw') delta = (-dx + dy) / 2;
      if (corner === 'ne') delta =  (dx - dy) / 2;
      if (corner === 'nw') delta = (-dx - dy) / 2;

      const newSize = clamp(startSize + delta, 0.05, 0.95);
      const sizeDiff = newSize - startSize;

      // Anchor the opposite corner: adjust position so that corner stays fixed
      let newPosX = startPosX;
      let newPosY = startPosY;
      if (corner === 'ne' || corner === 'nw') newPosY = startPosY - sizeDiff;
      if (corner === 'sw' || corner === 'nw') newPosX = startPosX - sizeDiff;

      state.size = newSize;
      state.posX = clamp(newPosX, 0, 1);
      state.posY = clamp(newPosY, 0, 1);

      refs.sizeRange.value     = Math.round(state.size * 100);
      refs.sizeVal.textContent = Math.round(state.size * 100) + '%';
      updateArtworkTransform();
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  /* Resize from any corner – Touch */
  function onCornerResizeTouchStart(e, corner) {
    e.preventDefault();
    e.stopPropagation();

    const rect      = refs.canvas.getBoundingClientRect();
    const startX    = e.touches[0].clientX;
    const startY    = e.touches[0].clientY;
    const startSize = state.size;
    const startPosX = state.posX;
    const startPosY = state.posY;
    const minDim    = Math.min(rect.width, rect.height);

    function onMove(te) {
      const dx = (te.touches[0].clientX - startX) / minDim;
      const dy = (te.touches[0].clientY - startY) / minDim;

      let delta;
      if (corner === 'se') delta =  (dx + dy) / 2;
      if (corner === 'sw') delta = (-dx + dy) / 2;
      if (corner === 'ne') delta =  (dx - dy) / 2;
      if (corner === 'nw') delta = (-dx - dy) / 2;

      const newSize = clamp(startSize + delta, 0.05, 0.95);
      const sizeDiff = newSize - startSize;

      let newPosX = startPosX;
      let newPosY = startPosY;
      if (corner === 'ne' || corner === 'nw') newPosY = startPosY - sizeDiff;
      if (corner === 'sw' || corner === 'nw') newPosX = startPosX - sizeDiff;

      state.size = newSize;
      state.posX = clamp(newPosX, 0, 1);
      state.posY = clamp(newPosY, 0, 1);

      refs.sizeRange.value     = Math.round(state.size * 100);
      refs.sizeVal.textContent = Math.round(state.size * 100) + '%';
      updateArtworkTransform();
    }
    function onEnd() {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    }
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }

    /* ─────────────────────────────────────────────
     Save Preview as Image
     FIX: use the canvas element's actual CSS pixel
     dimensions so the output exactly matches what
     the customer sees — no scaling or skew.
  ───────────────────────────────────────────── */
  function savePreviewImage() {
    if (!state.artworkSrc) {
      showToast('Upload your artwork first');
      return;
    }
    flattenToBlob(function (blob) {
      if (!blob) { showToast('Could not generate image'); return; }

      var filename = 'mangrovery-custom-preview.png';

      // On iOS Safari, navigator.share() with files triggers the native share sheet
      // which includes "Save Image" → Photos. This is the only way to reach the
      // Photos app directly from a browser on iPhone — the <a download> approach
      // goes to Files/browser downloads instead.
      var canShare = (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [new File([blob], filename, { type: 'image/png' })] })
      );

      if (canShare) {
        var file = new File([blob], filename, { type: 'image/png' });
        navigator.share({
          files: [file],
          title: 'My Custom Product Preview'
        }).then(function () {
          showToast('Preview shared!');
        }).catch(function (err) {
          // User cancelled the share sheet — not an error, do nothing
          if (err.name !== 'AbortError') {
            // Share failed unexpectedly — fall back to download
            triggerDownload(blob, filename);
          }
        });
      } else {
        // Desktop, Android Chrome, or any browser without file share support
        triggerDownload(blob, filename);
      }
    });
  }

  function triggerDownload(blob, filename) {
    var url = URL.createObjectURL(blob);
    var a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Preview saved!');
  }

  /* ─────────────────────────────────────────────
     Reset
  ───────────────────────────────────────────── */
  function resetAll() {
    state.selectedProductIdx = state.products.length === 1 ? 0 : -1;
    state.productSrc  = state.products.length === 1 ? state.products[0].url : null;
    state.artworkSrc  = null;
    state.artworkName = '';
    state.posX   = 0.3;
    state.posY   = 0.25;
    state.size   = 0.35;
    state.opacity = 40;
    state.flipH    = false;
    state.flipV    = false;
    state.rotation = 0;
    state.selected = false;
    state.panel1Collapsed = false;
    state.panel2Collapsed = false;
    state.panel3Collapsed = false;

    refs.sizeRange.value     = 35;
    refs.sizeVal.textContent = '35%';
    refs.flipH.classList.remove('active');
    refs.flipV.classList.remove('active');
    if (refs.rotationRange) { refs.rotationRange.value = 0; refs.rotationVal.textContent = '0°'; }

    // Hide corner handles immediately — render() hides them via renderCanvas
    // but they need to go before any repaint to avoid flash
    if (refs.cornerHandles) {
      refs.cornerHandles.forEach(function (el) { el.style.display = 'none'; });
    }

    // Re-enable submit button — clear submitted flag so customer can resubmit
    if (refs.submitBtn) {
      refs.submitBtn.disabled = true;
      refs.submitBtn.dataset.reason = 'no-artwork';
      delete refs.submitBtn.dataset.submitted;
    }

    render();

    // Clear form fields and status AFTER render() to prevent autocomplete
    // from re-populating name/email before the DOM settles.
    // Temporarily set autocomplete="off" during the clear — then restore
    // after a tick — to stop the browser refilling from saved autofill data.
    if (refs.nameInput) {
      refs.nameInput.setAttribute('autocomplete', 'off');
      refs.nameInput.value = '';
    }
    if (refs.emailInput) {
      refs.emailInput.setAttribute('autocomplete', 'off');
      refs.emailInput.value = '';
    }
    if (refs.messageInput) refs.messageInput.value = '';
    setFormStatus('', '');

    setTimeout(function () {
      if (refs.nameInput)  refs.nameInput.setAttribute('autocomplete',  'name');
      if (refs.emailInput) refs.emailInput.setAttribute('autocomplete', 'email');
    }, 100);
  }

  /* ─────────────────────────────────────────────
     Helpers
  ───────────────────────────────────────────── */
  function showToast(msg) {
    refs.toast.textContent = msg;
    refs.toast.classList.add('show');
    setTimeout(function () { refs.toast.classList.remove('show'); }, 2500);
  }

  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  function isMobile() { return window.innerWidth <= 820; }

  // Decode HTML entities that Liquid's | escape filter encodes into data attributes
  function decodeAttr(str) {
    if (!str) return '';
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─────────────────────────────────────────────
     Image flattening (shared by download + upload)
  ───────────────────────────────────────────── */
  function flattenToBlob(callback) {
    // Output at the product image's natural pixel dimensions.
    // This eliminates all aspect-ratio rounding — we work in the product
    // image's own coordinate space so it always fills the output exactly.
    // Artwork coordinates (stored as fractions of the screen canvas) are
    // scaled up proportionally to match.

    var screenW = refs.canvas.offsetWidth;
    var screenH = refs.canvas.offsetHeight;

    function loadImg(src, crossOrigin) {
      return new Promise(function (resolve) {
        var img = new Image();
        if (crossOrigin) img.crossOrigin = 'anonymous';
        img.onload  = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }

    loadImg(state.productSrc, true).then(function (pImg) {
      // Use the product image's natural dimensions as the output size
      var outW = pImg ? pImg.naturalWidth  : screenW;
      var outH = pImg ? pImg.naturalHeight : screenH;

      var out = document.createElement('canvas');
      out.width  = outW;
      out.height = outH;
      var ctx = out.getContext('2d');

      if (pImg) {
        // Draw product at full natural resolution — perfect quality, no stretching
        ctx.drawImage(pImg, 0, 0, outW, outH);
      }

      if (!state.artworkSrc) { out.toBlob(callback, 'image/png'); return; }

      loadImg(state.artworkSrc, false).then(function (aImg) {
        if (aImg) {
          // Scale artwork coordinates from screen canvas space to output space.
          // posX/posY are fractions of screen canvas; size is relative to the
          // smaller screen dimension. Scale each proportionally to output dims.
          var scaleX = outW / screenW;
          var scaleY = outH / screenH;

          var screenSz = state.size * Math.min(screenW, screenH);
          var screenAx = state.posX * screenW;
          var screenAy = state.posY * screenH;

          var sz = screenSz * Math.min(scaleX, scaleY);
          var ax = screenAx * scaleX;
          var ay = screenAy * scaleY;

          var cfg = refs.config;

          // Step 2: draw artwork onto offscreen canvas, applying rotation first
          var rotation = state.rotation || 0;
          var rad      = rotation * Math.PI / 180;
          // Expand ofc to fit the artwork at any rotation without clipping
          var rotDiag  = Math.ceil(sz * (Math.abs(Math.cos(rad)) + Math.abs(Math.sin(rad))));
          var ofc    = document.createElement('canvas');
          ofc.width  = rotDiag;
          ofc.height = rotDiag;
          var ofCtx  = ofc.getContext('2d');
          ofCtx.translate(rotDiag / 2, rotDiag / 2);
          if (rotation) ofCtx.rotate(rad);
          ofCtx.drawImage(aImg, -sz / 2, -sz / 2, sz, sz);
          ofCtx.setTransform(1, 0, 0, 1, 0, 0); // reset transform for pixel ops

          var sepia      = parseInt(cfg.artworkSepia      || '0') / 100;
          var hue        = parseInt(cfg.artworkHue        || '0');
          var saturate   = parseInt(cfg.artworkSaturate   || '100') / 100;
          var brightness = parseInt(cfg.artworkBrightness || '100') / 100;

          if (sepia > 0 || hue !== 0 || saturate !== 1 || brightness !== 1) {
            var imgData = ofCtx.getImageData(0, 0, ofc.width, ofc.height);
            var d = imgData.data;
            var hRad = hue * Math.PI / 180;
            var cosH = Math.cos(hRad), sinH = Math.sin(hRad);
            for (var i = 0; i < d.length; i += 4) {
              var r = d[i], g = d[i+1], b = d[i+2];
              if (sepia > 0) {
                var sr = Math.min(255, r*(1-0.607*sepia) + g*0.769*sepia + b*0.189*sepia);
                var sg = Math.min(255, r*0.349*sepia + g*(1-0.314*sepia) + b*0.168*sepia);
                var sb = Math.min(255, r*0.272*sepia + g*0.534*sepia + b*(1-0.869*sepia));
                r = sr; g = sg; b = sb;
              }
              if (hue !== 0) {
                var nr = r*(0.213+cosH*0.787-sinH*0.213) + g*(0.715-cosH*0.715-sinH*0.715) + b*(0.072-cosH*0.072+sinH*0.928);
                var ng = r*(0.213-cosH*0.213+sinH*0.143) + g*(0.715+cosH*0.285+sinH*0.140) + b*(0.072-cosH*0.072-sinH*0.283);
                var nb = r*(0.213-cosH*0.213-sinH*0.787) + g*(0.715-cosH*0.715+sinH*0.715) + b*(0.072+cosH*0.928+sinH*0.072);
                r = Math.max(0,Math.min(255,nr)); g = Math.max(0,Math.min(255,ng)); b = Math.max(0,Math.min(255,nb));
              }
              if (saturate !== 1) {
                var grey = 0.2126*r + 0.7152*g + 0.0722*b;
                r = Math.max(0,Math.min(255, grey+(r-grey)*saturate));
                g = Math.max(0,Math.min(255, grey+(g-grey)*saturate));
                b = Math.max(0,Math.min(255, grey+(b-grey)*saturate));
              }
              if (brightness !== 1) {
                r = Math.max(0,Math.min(255, r*brightness));
                g = Math.max(0,Math.min(255, g*brightness));
                b = Math.max(0,Math.min(255, b*brightness));
              }
              d[i]=r; d[i+1]=g; d[i+2]=b;
            }
            ofCtx.putImageData(imgData, 0, 0);
          }

          // Step 3: draw filtered (and rotated) artwork onto main canvas
          // ofc may be larger than sz due to rotation diagonal expansion —
          // centre it on the artwork position
          var ofcOffset = (ofc.width - sz) / 2;
          ctx.save();
          ctx.globalAlpha = state.opacity / 100;
          if (state.flipH || state.flipV) {
            ctx.translate(ax + sz / 2, ay + sz / 2);
            ctx.scale(state.flipH ? -1 : 1, state.flipV ? -1 : 1);
            ctx.drawImage(ofc, -ofc.width / 2, -ofc.height / 2);
          } else {
            ctx.drawImage(ofc, ax - ofcOffset, ay - ofcOffset);
          }
          ctx.restore();
        }
        out.toBlob(callback, 'image/png');
      });
    });
  }


  /* ─────────────────────────────────────────────
     Submit Order — posts multipart form to the
     Cloudflare Worker which holds all credentials
  ───────────────────────────────────────────── */
  function submitOrder() {
    var name    = refs.nameInput.value.trim();
    var email   = refs.emailInput.value.trim();
    var message = refs.messageInput.value.trim();
    var config  = refs.config;

    // Honeypot check
    if (refs.honeypot && refs.honeypot.value) {
      refs.submitBtn.disabled = true;
      return;
    }

    if (!name)    { setFormStatus('Name is required.', 'error'); refs.nameInput.focus(); return; }
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      setFormStatus('A valid email address is required.', 'error');
      refs.emailInput.focus(); return;
    }
    if (!message) { setFormStatus('A message is required.', 'error'); refs.messageInput.focus(); return; }
    if (!state.artworkSrc) { setFormStatus('Please upload your artwork first.', 'error'); return; }
    if (!config.workerUrl) {
      setFormStatus('Submission is not configured. Please contact the store owner.', 'error'); return;
    }

    refs.submitBtn.disabled = true;
    refs.authNote.textContent = '';
    setFormStatus('Preparing your files…', 'info');

    var orderData = {
      name:         name,
      email:        email,
      message:      message,
      productLabel: state.products[state.selectedProductIdx]
                    ? state.products[state.selectedProductIdx].label : 'Unknown',
      posX:     Math.round(state.posX * 100),
      posY:     Math.round(state.posY * 100),
      size:     Math.round(state.size * 100),
      flipH:    state.flipH,
      flipV:    state.flipV,
      rotation: state.rotation || 0,
    };

    setFormStatus('Generating preview image…', 'info');

    // Flatten the canvas preview to a blob, then fetch the artwork blob,
    // then POST everything to the Cloudflare Worker as multipart form data.
    flattenToBlob(function (previewBlob) {
      if (!previewBlob) {
        setFormStatus('Could not generate preview image.', 'error');
        refs.submitBtn.disabled = false;
        return;
      }

      setFormStatus('Uploading your files…', 'info');

      fetch(state.artworkSrc)
        .then(function (r) { return r.blob(); })
        .then(function (artworkBlob) {
          var form = new FormData();
          form.append('name',         orderData.name);
          form.append('email',        orderData.email);
          form.append('message',      orderData.message);
          form.append('productLabel', orderData.productLabel);
          form.append('posX',         orderData.posX);
          form.append('posY',         orderData.posY);
          form.append('size',         orderData.size);
          form.append('flipH',        orderData.flipH);
          form.append('flipV',        orderData.flipV);
          form.append('rotation',     orderData.rotation);
          form.append('storeName',    config.storeName || '');
          form.append('userAgent',    navigator.userAgent || '');
          form.append('previewImage', previewBlob,  'preview.png');
          form.append('artworkImage', artworkBlob,  'artwork.png');

          return fetch(config.workerUrl, { method: 'POST', body: form });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('Worker responded with ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (!data.ok) throw new Error(data.error || 'Unknown error');
          setFormStatus(config.successMessage || 'Image submitted! We will be in touch shortly.', 'success');
          refs.formStatus.style.color = config.successColor || '#6aaa7e';
          refs.submitBtn.disabled = true;
          refs.submitBtn.dataset.submitted = 'true';
          delete refs.submitBtn.dataset.reason;
        })
        .catch(function (err) {
          console.error('CPP submit error:', err);
          setFormStatus('Something went wrong: ' + err.message + '. Please try again.', 'error');
          refs.submitBtn.disabled = false;
        });
    });
  }

    function setFormStatus(msg, type) {
    if (!refs.formStatus) return;
    refs.formStatus.textContent = msg;
    refs.formStatus.className = 'cpp-form-status ' + (type || '');
  }

  /* ─────────────────────────────────────────────
     Artwork filter helper
  ───────────────────────────────────────────── */
  function buildArtworkFilter(cfg) {
    var parts = [];
    var sepia      = parseInt(cfg.artworkSepia      || '0');
    var hue        = parseInt(cfg.artworkHue        || '0');
    var saturate   = parseInt(cfg.artworkSaturate   || '100');
    var brightness = parseInt(cfg.artworkBrightness || '100');
    if (sepia > 0)          parts.push('sepia(' + sepia + '%)');
    if (hue !== 0)          parts.push('hue-rotate(' + hue + 'deg)');
    if (saturate !== 100)   parts.push('saturate(' + saturate + '%)');
    if (brightness !== 100) parts.push('brightness(' + brightness + '%)');
    return parts.length ? parts.join(' ') : 'none';
  }

  /* ─────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
